import { execFileSync } from 'child_process'
import fs from 'fs'
import path from 'path'

import {
  CONFIG_FILE,
  PLACEHOLDER_SCRIPTS,
  TEST_DEV_DEPENDENCIES,
  TEST_GITIGNORE_ENTRIES,
  TEST_SCRIPTS,
  TOKEN_DIR,
} from '../../constants'
import { PromptAbortedError, PromptRequiredError } from '../../errors'
import { ensureServerUrl } from '../../project/config'
import { packageRoot } from '../../templates/package-root'
import { PORTABLE_CLI_COMMAND } from '../../utils/invocation'
import { resolveServerTarget } from '../server-target'
import type { OperationOptions } from '../types'
import { emitter } from '../types'
import type { CreateServiceOptions, ScaffoldedService } from './create-service'
import { resolveTemplateId, scaffoldFromTemplate } from './create-service'
import { writeTestingFiles } from './testing'

// `init` bootstraps the project the other commands assume: an npm package with the CLI as a devDependency,
// a `flowrunner.json` naming a server, a `.gitignore` that keeps the token out of git, and the unit-test
// harness a service is written against (see `testing.ts`).
//
// Nothing here overwrites. The command is meant to be safe to run in a directory that already has files —
// adding FlowRunner to an existing repository is the same operation as starting from nothing, minus the
// files that are already there.

export type InitEvent =
  | { type: 'target'; projectRoot: string; lines: string[] }
  | { type: 'files'; created: string[]; skipped: string[]; serverUrl: string }
  | { type: 'repository'; committed: boolean }
  | { type: 'cancelled' }
  | { type: 'warning'; message: string }

export interface InitOptions extends Omit<CreateServiceOptions, 'onEvent'>, OperationOptions<InitEvent> {
  /** A template to scaffold a first service from. Omitted creates just the project. */
  template?: string
  /**
   * Written into `flowrunner.json`; defaults to the CLI's own default cluster.
   *
   * A cluster name (`prod`/`dev`/`local`) or a full URL.
   */
  server?: string
  /** Set false to skip `npm install`. */
  install?: boolean
  /**
   * Set false to skip the unit-test harness — the sandbox, the jest config, and their dependencies.
   *
   * For a project that already has a test runner it does not want two of.
   */
  tests?: boolean
  /** Set false to skip initialising a git repository and making the first commit. */
  git?: boolean
  /**
   * Confirm writing into a directory that already holds something.
   *
   * `init` never overwrites, but landing a project on top of an unrelated one — a mistyped `--dir`, a
   * stray `..` — is worth a second look. Omitted means nothing can be asked, so a directory that needs
   * confirming throws instead.
   */
  onConfirmTarget?: (target: TargetDescription) => Promise<boolean>
  /** Progress from the service scaffold, when a template was given. */
  onServiceEvent?: CreateServiceOptions['onEvent']
}

export interface TargetDescription {
  projectRoot: string
  lines: string[]
  defaultYes: boolean
}

export interface InitResult {
  projectRoot: string
  /** The target was not confirmed, so nothing was written. */
  cancelled: boolean
  created: string[]
  skipped: string[]
  serverUrl: string
  /** The harness files that were written, relative to the project root. Empty under `tests: false`. */
  testingFiles: string[]
  service?: ScaffoldedService
  installed: boolean
  /** 'created' — a new repository; 'existing' — left alone; 'skipped' — `--no-git`; 'failed' — git refused. */
  repository: 'created' | 'existing' | 'skipped' | 'failed'
  committed: boolean
}

const GITIGNORE_ENTRIES = ['node_modules/', `${ TOKEN_DIR }/`, ...TEST_GITIGNORE_ENTRIES]

/** Reports something that went wrong without stopping the init. */
type Warn = (message: string) => void

/** The CLI's own version, so a generated project pins the version that generated it. */
function cliVersion(): string {
  return JSON.parse(fs.readFileSync(path.join(packageRoot(), 'package.json'), 'utf-8')).version
}

/** A directory name turned into something npm will accept as a package name. */
function packageNameFrom(directory: string): string {
  const name = path.basename(path.resolve(directory))
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[._-]+|[-._]+$/g, '')

  return name || 'flowrunner-extensions'
}

/**
 * Add every entry of `additions` that the section does not already have, and report whether anything
 * changed.
 *
 * "Already have" is by key, never by value: a project pinning jest 29, or defining its own `test` script
 * that runs something else first, has made a decision — and `init` is not the command that revisits it.
 *
 * `placeholders` is the one exception, and it is not a value judgement: `npm init -y` writes a `test` script
 * that exits 1, which is a stand-in for the absence of one rather than anything the project chose.
 */
function addMissing(
  section: Record<string, string>,
  additions: Record<string, string>,
  placeholders: Record<string, string> = {}
): boolean {
  let changed = false

  for (const [name, value] of Object.entries(additions)) {
    if (section[name] === undefined || section[name] === placeholders[name]) {
      section[name] = value
      changed = true
    }
  }

  return changed
}

function writePackageJson(projectRoot: string, tests: boolean): 'created' | 'updated' | 'skipped' {
  const fullPath = path.join(projectRoot, 'package.json')
  const version = cliVersion()

  const devDependencies = { 'flowrunner-cli': version, ...(tests ? TEST_DEV_DEPENDENCIES : {}) }
  const scripts = { deploy: 'flowrunner deploy --all', ...(tests ? TEST_SCRIPTS : {}) }

  if (!fs.existsSync(fullPath)) {
    const manifest = {
      name       : packageNameFrom(projectRoot),
      version    : '0.0.1',
      private    : true,
      description: 'FlowRunner custom extensions.',
      scripts,
      devDependencies,
    }

    fs.writeFileSync(fullPath, JSON.stringify(manifest, null, 2) + '\n', 'utf-8')

    return 'created'
  }

  // An existing project keeps its own manifest, including a version of the CLI or of jest it has
  // deliberately pinned. What is worth adding is what the project cannot work without: the CLI itself,
  // without which `npx flowrunner` resolves to whatever is globally installed — or to nothing — and the
  // packages the harness files just copied in would fail to require.
  const manifest = JSON.parse(fs.readFileSync(fullPath, 'utf-8'))

  // A CLI already in `dependencies` counts as installed and is left exactly where it is. Where a project
  // puts it is its own decision, and listing it in both sections is how npm ends up resolving two.
  const additions = manifest.dependencies?.['flowrunner-cli']
    ? Object.fromEntries(Object.entries(devDependencies).filter(([name]) => name !== 'flowrunner-cli'))
    : devDependencies

  manifest.devDependencies ??= {}
  manifest.scripts ??= {}

  // Both run — `.some()` on an array, not `||` — so a manifest needing only the scripts still gets them.
  const changed = [
    addMissing(manifest.devDependencies, additions),
    addMissing(manifest.scripts, tests ? TEST_SCRIPTS : {}, PLACEHOLDER_SCRIPTS),
  ].some(Boolean)

  if (!changed) {
    return 'skipped'
  }

  fs.writeFileSync(fullPath, JSON.stringify(manifest, null, 2) + '\n', 'utf-8')

  return 'updated'
}

function writeGitignore(projectRoot: string): 'created' | 'updated' | 'skipped' {
  const fullPath = path.join(projectRoot, '.gitignore')

  if (!fs.existsSync(fullPath)) {
    fs.writeFileSync(fullPath, GITIGNORE_ENTRIES.join('\n') + '\n', 'utf-8')

    return 'created'
  }

  const content = fs.readFileSync(fullPath, 'utf-8')
  const lines = content.split('\n').map(line => line.trim())
  // A bare `node_modules` covers `node_modules/`; compare on the stem so an existing entry is respected.
  const missing = GITIGNORE_ENTRIES.filter(entry => !lines.includes(entry) && !lines.includes(entry.replace(/\/$/, '')))

  if (missing.length === 0) {
    return 'skipped'
  }

  fs.appendFileSync(fullPath, `${ content.endsWith('\n') ? '' : '\n' }${ missing.join('\n') }\n`)

  return 'updated'
}

const README = (name: string) => `# ${ name }

FlowRunner custom extensions for this workspace.

Each folder under \`services/\` is one extension and one npm package: it has its own \`package.json\` and its
own \`node_modules\`, so \`npm install\` for a service is run **inside that service's folder**. It is deployed
and versioned on its own, and deploying one leaves every other service in the workspace untouched — which is
what lets several projects deploy into the same workspace.

\`\`\`bash
${ PORTABLE_CLI_COMMAND } cs            # scaffold another service
${ PORTABLE_CLI_COMMAND } login         # authorize against the console (once)
${ PORTABLE_CLI_COMMAND } deploy -s <id># build one service's definition and upload it (--all for every one)
${ PORTABLE_CLI_COMMAND } pull          # bring the workspace's services down into services/
${ PORTABLE_CLI_COMMAND } init-claude   # wire the FlowRunner agents into Claude Code

cd services/<id> && npm install   # add a dependency to one service
npm test                 # run the unit suites in services/*/tests/
\`\`\`

With \`npm i -g flowrunner-cli\` you can drop the \`npx\` and just write \`flowrunner cs\`. The global copy only
launches the one in this project's \`node_modules\`, so the version pinned in \`package.json\` is still the one
that runs.

To move this project to a newer CLI, upgrade the dependency and commit it — that is what decides which code
runs, for you and for CI:

\`\`\`bash
npm i -D flowrunner-cli@<version>
\`\`\`

Updating the global copy (\`npm i -g flowrunner-cli@latest\`) is rarely needed and changes nothing here.

Tests run each service the way the platform does, with HTTP intercepted at the socket — see
\`sandbox/index.js\`, which \`${ PORTABLE_CLI_COMMAND } init\` copied here for you to edit.

The authoring reference the CLI ships lives in
\`node_modules/flowrunner-cli/ai-docs/instructions/service-code-format/\`. Point your AI agent at it — or run
\`${ PORTABLE_CLI_COMMAND } init-claude\` and Claude Code is wired up for you.

\`flowrunner.json\` names the server and the workspace and belongs in git.
\`.flowrunner/token\` is the deploy token and must not.
`

function writeReadme(projectRoot: string): 'created' | 'skipped' {
  const fullPath = path.join(projectRoot, 'README.md')

  if (fs.existsSync(fullPath)) {
    return 'skipped'
  }

  fs.writeFileSync(fullPath, README(packageNameFrom(projectRoot)), 'utf-8')

  return 'created'
}

function git(projectRoot: string, args: string[]): void {
  execFileSync('git', args, { cwd: projectRoot, stdio: 'pipe' })
}

/** Whether the directory is already tracked — by its own repository or by one further up. */
function insideRepository(projectRoot: string): boolean {
  try {
    git(projectRoot, ['rev-parse', '--is-inside-work-tree'])

    return true
  } catch {
    return false
  }
}

/**
 * Start a repository, unless the directory is already in one.
 *
 * An existing repository is left completely alone — `init` has no business running `git init` inside a
 * monorepo subdirectory, and it must never touch a working tree that may hold someone's unrelated changes.
 */
function gitInit(projectRoot: string, warn: Warn): 'created' | 'existing' | 'failed' {
  if (insideRepository(projectRoot)) {
    return 'existing'
  }

  try {
    // `-b main` also silences git's default-branch hint. Older gits do not know the flag.
    try {
      git(projectRoot, ['init', '-b', 'main'])
    } catch {
      git(projectRoot, ['init'])
    }

    return 'created'
  } catch {
    warn('git init failed — is git installed? The project files are fine either way.')

    return 'failed'
  }
}

/**
 * The first commit, made after `npm install` so the lockfile is part of it.
 *
 * Only ever runs in a repository this command just created, where staging everything is safe by
 * construction: there is nothing else in it.
 */
function gitCommit(projectRoot: string, warn: Warn): boolean {
  try {
    git(projectRoot, ['add', '-A'])
    git(projectRoot, ['commit', '-m', 'Initial commit'])

    return true
  } catch {
    // Most often an unconfigured user.name / user.email. Setting one ourselves would stamp an identity the
    // user never chose onto their own history, so the files stay staged and they decide.
    warn('git commit failed — the files are staged; commit them once git knows who you are.')

    return false
  }
}

function npmInstall(projectRoot: string, warn: Warn): boolean {
  const npm = process.platform === 'win32' ? 'npm.cmd' : 'npm'

  try {
    execFileSync(npm, ['install'], { cwd: projectRoot, stdio: 'inherit' })

    return true
  } catch {
    // The project files are already correct, so a failed install is a warning and not a failed init: the
    // user can run `npm install` themselves once they have fixed whatever npm complained about.
    warn('npm install failed — run it yourself in the project directory.')

    return false
  }
}

/**
 * What the user is about to do to a directory, in the terms that would make them notice a mistake: the
 * absolute path, whether it exists, whether anything is in it, and whether it is already a project.
 */
export function describeTarget(projectRoot: string): TargetDescription {
  if (!fs.existsSync(projectRoot)) {
    return { projectRoot, lines: ['a new directory'], defaultYes: true }
  }

  if (!fs.statSync(projectRoot).isDirectory()) {
    return { projectRoot, lines: ['not a directory'], defaultYes: false }
  }

  const entries = fs.readdirSync(projectRoot).filter(entry => entry !== '.git')

  if (entries.length === 0) {
    return { projectRoot, lines: ['an empty directory'], defaultYes: true }
  }

  const lines = [`an existing directory — ${ entries.length } ${ entries.length === 1 ? 'entry' : 'entries' }`]

  if (entries.includes('package.json')) {
    lines.push('already an npm project — its package.json will be kept, with the CLI added as a devDependency')
  }

  if (entries.includes('services')) {
    lines.push('already has services/')
  }

  if (insideRepository(projectRoot)) {
    lines.push('already tracked by git — no repository will be created and nothing will be committed')
  }

  // Anything already in there is a reason to look twice: `init -d ..` or a mistyped path lands here, and
  // the files this writes would then join a project that never asked for them.
  return { projectRoot, lines, defaultYes: false }
}

/** Create the project the other operations assume, optionally with a first service in it. */
export async function init(options: InitOptions = {}): Promise<InitResult> {
  const projectRoot = path.resolve(options.cwd ?? '.')
  const emit = emitter(options)
  const warn = (message: string) => emit({ type: 'warning', message })

  // Both resolved before anything is written, so an unknown template or an unknown server fails without
  // leaving a half-made project behind.
  const template = options.template ? resolveTemplateId(options.template) : null
  const requestedServerUrl = options.server ? resolveServerTarget(options.server) : undefined

  const target = describeTarget(projectRoot)

  emit({ type: 'target', projectRoot, lines: target.lines })

  // A directory that already holds something, and no way to ask about it: neither answer can be assumed.
  // Writing would be a silent surprise, and doing nothing would be a silent no-op — so say what is needed.
  if (!options.acceptDefaults && !options.onConfirmTarget && !target.defaultYes) {
    throw new PromptRequiredError(
      'confirm-target',
      `${ projectRoot } already has files in it, and there is no way to confirm writing a project there.`
    )
  }

  // `acceptDefaults` already means "ask nothing".
  if (!options.acceptDefaults && options.onConfirmTarget && !await options.onConfirmTarget(target)) {
    emit({ type: 'cancelled' })

    return {
      projectRoot,
      cancelled   : true,
      created     : [],
      skipped     : [],
      serverUrl   : '',
      testingFiles: [],
      installed   : false,
      repository  : 'skipped',
      committed   : false,
    }
  }

  if (!fs.existsSync(projectRoot)) {
    fs.mkdirSync(projectRoot, { recursive: true })
  }

  if (!fs.statSync(projectRoot).isDirectory()) {
    throw new PromptAbortedError(`${ projectRoot } is not a directory.`)
  }

  const created: string[] = []
  const skipped: string[] = []

  const record = (file: string, outcome: 'created' | 'updated' | 'skipped') => {
    (outcome === 'skipped' ? skipped : created).push(outcome === 'updated' ? `${ file } (updated)` : file)
  }

  const tests = options.tests !== false

  record('package.json', writePackageJson(projectRoot, tests))
  record('.gitignore', writeGitignore(projectRoot))

  const server = ensureServerUrl(projectRoot, requestedServerUrl)

  record(CONFIG_FILE, server.created ? 'created' : server.updated ? 'updated' : 'skipped')
  record('README.md', writeReadme(projectRoot))

  // The harness is part of the project rather than something bolted on afterwards, so it is written with
  // the rest of the scaffolding and reported in the same list.
  const testing = tests ? writeTestingFiles(projectRoot) : { created: [], skipped: [] }

  testing.created.forEach(file => record(file, 'created'))
  testing.skipped.forEach(file => record(file, 'skipped'))

  emit({ type: 'files', created, skipped, serverUrl: server.serverUrl })

  const service = template
    ? await scaffoldFromTemplate(template, { ...options, onEvent: options.onServiceEvent }, projectRoot)
    : undefined

  // Before the install, so a repository exists to commit into; the commit itself waits until after it, so
  // package-lock.json lands in the first commit rather than as an immediate diff.
  const repository = options.git === false ? 'skipped' : gitInit(projectRoot, warn)
  const installed = options.install === false ? false : npmInstall(projectRoot, warn)
  const committed = repository === 'created' && gitCommit(projectRoot, warn)

  if (repository === 'created') {
    emit({ type: 'repository', committed })
  }

  return {
    projectRoot,
    cancelled   : false,
    created,
    skipped,
    serverUrl   : server.serverUrl,
    testingFiles: testing.created,
    service,
    installed,
    repository,
    committed,
  }
}

export type { TestingFilesResult } from './testing'
export { testingFilesDirectory, writeTestingFiles } from './testing'
